import logoAmex from '@/assets/images/logo_amex.png';
import logoDinersClub from '@/assets/images/logo_diners_club.png';
import logoDiscover from '@/assets/images/logo_discover.png';
import logoElo from '@/assets/images/logo_elo.png';
import logoJcb from '@/assets/images/logo_jcb.png';
import logoMaster from '@/assets/images/logo_mastercard.png';
import logoVisa from '@/assets/images/logo_visa.png';
import { Button, Modal } from '@/components';
import { AuthWrapper } from '@/features/core/auth/screens/AuthWrapper';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import CardDb from '@/model/CardDb';
import CardType from '@/model/CardType';
import { CardValidationResponse } from '@/model/CardValidationResponse';
import CardValidationResponseStatus from '@/model/CardValidationResponseStatus';
import React, { JSX, ReactNode } from 'react';
import { ModalAlertAddCredCard } from '../../components/ModalAlertAddCredCard';
import { ModalErrorMessage } from '../../components/ModalErrorMessage';
import { FormInputCard, ShowModalPayment } from '../../types';

interface StateProps {
  cardPendingValidation: boolean;
  cardValidationResponses: CardValidationResponse[];
  cardSelected?: CardDb;
  formData: FormData;
  formErrors: FormErrors;
  visible: boolean;
  title: ReactNode | string;
  shouldShowModal: ShowModalPayment;
  titleError: string;
  messageError: string[];
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onCancel: () => void;
  onConfirm: () => void;
  onCheckout: () => void;
  onToggle: () => void;
  onClose: () => void;
  goAddCard: () => void;
}

type Props = StateProps & DispatchProps;

export const ValidatedCreditCardUI: React.FC<Props> = ({
  cardPendingValidation,
  cardValidationResponses,
  cardSelected,
  formData,
  onChangeFormInput,
  formErrors,
  onCancel,
  onConfirm,
  onCheckout,
  visible,
  title,
  onToggle,
  shouldShowModal,
  onClose,
  goAddCard,
  titleError,
  messageError,
}): JSX.Element => {
  const getCardLogo = (cardType: CardType): string => {
    let logo = logoMaster;
    switch (cardType) {
      case CardType.MASTER_CARD:
        logo = logoMaster;
        break;
      case CardType.VISA:
        logo = logoVisa;
        break;
      case CardType.AMERICAN_EXPRESS:
        logo = logoAmex;
        break;
      case CardType.DISCOVER:
        logo = logoDiscover;
        break;
      case CardType.DINERS_CLUB:
        logo = logoDinersClub;
        break;
      case CardType.JCB:
        logo = logoJcb;
        break;
      case CardType.ELO:
        logo = logoElo;
        break;
      default:
        break;
    }
    return logo;
  };

  const showConfirm = (): boolean => {
    let show = false;
    if (cardPendingValidation) {
      cardValidationResponses.forEach(data => {
        if (
          data.status === CardValidationResponseStatus.APPROVED ||
          data.status === CardValidationResponseStatus.AWAITING_VALIDATION
        ) {
          show = true;
        }
      });
    }
    return show;
  };

  //   const goToNextScreen = (): void => {
  //     setCurrentScreen(currentScreen + 1);
  //   };

  return (
    <AuthWrapper isHomepage={false}>
      <div className="w-full max-w-3xl mx-auto pt-10 pb-24 px-4">
        {cardSelected && (
          <div className="flex flex-col gap-6 p-6 bg-white border border-gray-200 rounded-2xl shadow-sm">
            {cardPendingValidation ? (
              <>
                <div className="items-center justify-center text-center ">
                  <h3 className="text-2xl font-semibold text-neutro-b-400 mb-4">
                    Ativação do seu cartão
                  </h3>
                  <h4 className="text-xl sm:text-1xl font-semibold text-neutro-b-400">
                    Insira o valor encontrado na sua fatura:
                  </h4>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-full max-w-xs">
                    <input
                      aria-invalid="false"
                      value={formData[FormInputCard.value]}
                      placeholder="R$ 0,00"
                      maxLength={8}
                      onChange={e => onChangeFormInput(FormInputCard.value)(e.target.value)}
                      className="text-center h-12 w-full rounded-xl border border-red-500 bg-red-50 text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-400 transition"
                    />
                    {formErrors.errorInput && (
                      <p className="text-red-600 text-sm mt-1 text-center">
                        {formErrors.errorInput}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-[200] items-center justify-center gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <img
                    src={getCardLogo(cardSelected.cardType)}
                    alt="logo"
                    className="w-10 h-auto"
                  />
                  <span className="text-lg font-medium text-gray-700">
                    {cardSelected.exibitionNumber}
                  </span>
                </div>
                <p className="text-sm sm:text-base text-red-600 text-center mt-2 italic">
                  Obs: esse valor será estornado logo após. Ele serve apenas para validação.
                </p>
                <div className={`grid ${showConfirm() ? 'grid-cols-2' : 'grid-cols-1'} gap-4 mt-6`}>
                  <Button
                    type="button"
                    title="Cancelar"
                    buttonStyle="outlined"
                    size="lg"
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 transition rounded-xl"
                    onClick={onCancel}
                  />
                  <Button
                    type="button"
                    title="Validar"
                    buttonStyle="primary"
                    size="lg"
                    className="w-full bg-red-600 text-white hover:bg-red-700 transition rounded-xl"
                    onClick={onConfirm}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="items-center justify-center text-center ">
                  <h3 className="text-2xl font-semibold text-neutro-b-400 mb-4">Cartão Ativado</h3>
                  <h4 className="text-xl sm:text-1xl font-semibold text-neutro-b-400">
                    Seu cartão já esta ativado pode realizar sua comprar
                  </h4>
                </div>
                <Button
                  type="button"
                  title="Ir para carrinho de compras"
                  buttonStyle="outlined"
                  size="lg"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 transition rounded-xl"
                  onClick={onCheckout}
                />
              </>
            )}
          </div>
        )}

        <Modal
          visible={visible}
          title={title}
          onSetVisible={onToggle}
          actions={[]}
          className="md:w-[750px]"
          showCloseButton={shouldShowModal !== ShowModalPayment.VALIDATE_CREDIT_CARD}
        >
          {
            {
              [ShowModalPayment.VALIDATE_CREDIT_CARD]: (
                <>
                  <ModalAlertAddCredCard onClose={onClose} />
                </>
              ),
              [ShowModalPayment.VALIDATE_ERROR]: (
                <>
                  <ModalErrorMessage
                    goAddCard={goAddCard}
                    titleError={titleError}
                    messageError={messageError}
                  />
                </>
              ),
            }[shouldShowModal]
          }
        </Modal>
      </div>
    </AuthWrapper>
  );
};
